import { MultipleValues, PropertyInput } from './input-base';
import { LevelDesignerComponent } from '../../level-designer/level-designer.component';
import { PropertyEditor } from '../property-editor';

export class ColorInput extends PropertyInput {
    public dropdownVisible: boolean = false;
    private colorPickerVisible: boolean = false;
    private readonly colorPickerHeight = 120;
    private readonly hueStripHeight = 15;
    private readonly swatchSize = 20;
    private draggingHue = false;
    private draggingSatLight = false;
    private hue = 0;
    private saturation = 100;
    private lightness = 50;
    private cursorPosition: number = 0;
    private isSelected: boolean = false;

    constructor(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        value: any,
        isActive: boolean,
        levelDesigner: LevelDesignerComponent,
        propertyEditor: PropertyEditor
    ) {
        if(value == null) {
            value = 'ffffff';
        }
        super(ctx, x, y, width, height, value, isActive, levelDesigner, propertyEditor);
        if(value instanceof MultipleValues) {
            this.displayValue = value.toString();
        }
        else{
            this.displayValue = value;
            this.parseColor(value);
        }
    }

    private parseColor(color: string) {
        // Remove # if present
        color = color.replace('#', '');
        if (color.length === 6) {
            // Convert hex to HSL
            const r = parseInt(color.substring(0, 2), 16) / 255;
            const g = parseInt(color.substring(2, 4), 16) / 255;
            const b = parseInt(color.substring(4, 6), 16) / 255;
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const l = (max + min) / 2;
            let h, s;

            if (max === min) {
                h = s = 0;
            } else {
                const d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                switch (max) {
                    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                    case g: h = (b - r) / d + 2; break;
                    case b: h = (r - g) / d + 4; break;
                    default: h = 0;
                }
                h /= 6;
            }

            this.hue = h * 360;
            this.saturation = s * 100;
            this.lightness = l * 100;
        }
    }

    private hslToHex(h: number, s: number, l: number): string {
        h /= 360;
        s /= 100;
        l /= 100;
        let r, g, b;

        if (s === 0) {
            r = g = b = l;
        } else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1/6) return p + (q - p) * 6 * t;
                if (t < 1/2) return q;
                if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }

        const toHex = (x: number) => {
            const hex = Math.round(x * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };

        return toHex(r) + toHex(g) + toHex(b);
    }

    public draw(): void {
        // Draw base input
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(this.x, this.y, this.width, this.height);
        this.ctx.strokeStyle = this.isActive ? '#0078d4' : '#cccccc';
        this.ctx.strokeRect(this.x, this.y, this.width, this.height);

        // Draw text
        this.ctx.fillStyle = '#000000';
        this.ctx.font = '12px Arial';
        this.ctx.textBaseline = 'middle';
        
        // Handle text selection highlighting
        if (this.isActive && this.isSelected) {
            const textWidth = this.ctx.measureText(this.value).width;
            this.ctx.fillStyle = '#0078D7';
            this.ctx.fillRect(this.x + 5, this.y + 2, textWidth, this.height - 4);
            this.ctx.fillStyle = '#FFFFFF';
        }
        
        this.ctx.fillText(this.displayValue, this.x + 5, this.y + this.height / 2);
        
        // Draw cursor if active and not selected
        if (this.isActive && !this.isSelected) {
            const cursorX = this.x + 5 + this.ctx.measureText(this.value.slice(0, this.cursorPosition)).width;
            this.ctx.beginPath();
            this.ctx.moveTo(cursorX, this.y + 4);
            this.ctx.lineTo(cursorX, this.y + this.height - 4);
            this.ctx.strokeStyle = '#000000';
            this.ctx.lineWidth = 1;
            this.ctx.stroke();
        }

        // Draw color swatch
        const swatchX = this.x + this.width - this.swatchSize - 5;
        const swatchY = this.y + (this.height - this.swatchSize) / 2;
        
        this.ctx.fillStyle = '#' + this.value;
        this.ctx.fillRect(swatchX, swatchY, this.swatchSize, this.swatchSize);
        this.ctx.strokeStyle = '#cccccc';
        this.ctx.strokeRect(swatchX, swatchY, this.swatchSize, this.swatchSize);

        if(this.dropdownVisible) {
            this.drawDropdown();
        }
    }

    public drawDropdown(): void {
        if (!this.dropdownVisible) return;
        
        // Use the input's width for the color picker
        const pickerX = this.x;
        const pickerY = this.y + this.height + 5;

        // Draw background
        this.ctx.fillStyle = '#ffffff';
        this.ctx.strokeStyle = '#cccccc';
        this.ctx.fillRect(pickerX, pickerY, this.width, this.colorPickerHeight + this.hueStripHeight);
        this.ctx.strokeRect(pickerX, pickerY, this.width, this.colorPickerHeight + this.hueStripHeight);

        // Draw saturation/lightness picker
        const gradient = this.ctx.createLinearGradient(pickerX, pickerY, pickerX + this.width, pickerY);
        gradient.addColorStop(0, `hsl(${this.hue}, 0%, 50%)`);
        gradient.addColorStop(1, `hsl(${this.hue}, 100%, 50%)`);
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(pickerX, pickerY, this.width, this.colorPickerHeight);

        // Draw vertical gradient (black to white)
        const vertGradient = this.ctx.createLinearGradient(pickerX, pickerY, pickerX, pickerY + this.colorPickerHeight);
        vertGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        vertGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
        vertGradient.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
        vertGradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
        this.ctx.fillStyle = vertGradient;
        this.ctx.fillRect(pickerX, pickerY, this.width, this.colorPickerHeight);

        // Draw hue strip
        const hueY = pickerY + this.colorPickerHeight;
        for (let i = 0; i < this.width; i++) {
            const hue = (i / this.width) * 360;
            this.ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
            this.ctx.fillRect(pickerX + i, hueY, 1, this.hueStripHeight);
        }

        // Draw current position in saturation/lightness picker
        const satX = pickerX + (this.saturation / 100) * this.width;
        const lightY = pickerY + (1 - this.lightness / 100) * this.colorPickerHeight;
        this.ctx.beginPath();
        this.ctx.arc(satX, lightY, 4, 0, Math.PI * 2);
        this.ctx.strokeStyle = '#ffffff';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
        this.ctx.strokeStyle = '#000000';
        this.ctx.lineWidth = 1;
        this.ctx.stroke();

        // Draw current position in hue strip
        const hueX = pickerX + (this.hue / 360) * this.width;
        this.ctx.beginPath();
        this.ctx.moveTo(hueX, hueY - 3);
        this.ctx.lineTo(hueX - 3, hueY);
        this.ctx.lineTo(hueX + 3, hueY);
        this.ctx.closePath();
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fill();
        this.ctx.strokeStyle = '#000000';
        this.ctx.stroke();
    }

    public handleClick(x: number, y: number): boolean {
        // First check if the click is within the input bounds
        if (x < this.x || x > this.x + this.width || y < this.y || y > this.y + this.height) {
            // If color picker is visible, check if click is within its bounds
            if (this.dropdownVisible) {
                const pickerX = this.x;
                const pickerY = this.y + this.height + 5;
                const pickerBottom = pickerY + this.colorPickerHeight + this.hueStripHeight;

                if (x >= pickerX && x <= pickerX + this.width &&
                    y >= pickerY && y <= pickerBottom) {
                    // Handle color picker interaction
                    if (y <= pickerY + this.colorPickerHeight) {
                        // Clicked in saturation/lightness picker
                        this.saturation = ((x - pickerX) / this.width) * 100;
                        this.lightness = (1 - (y - pickerY) / this.colorPickerHeight) * 100;
                        this.updateColorFromHSL();
                        return true;
                    } else {
                        // Clicked in hue strip
                        this.hue = ((x - pickerX) / this.width) * 360;
                        this.updateColorFromHSL();
                        return true;
                    }
                }
                // If clicked outside picker, hide it
                this.dropdownVisible = false;
                return true;
            }
            return false;
        }

        // Check if clicking the color swatch
        const swatchX = this.x + this.width - this.swatchSize - 5;
        const swatchY = this.y + (this.height - this.swatchSize) / 2;
        if (x >= swatchX && x <= swatchX + this.swatchSize &&
            y >= swatchY && y <= swatchY + this.swatchSize) {
            this.isActive = true;
            this.dropdownVisible = !this.dropdownVisible;
            return true;
        }

        // Handle click in text area
        this.isActive = true;
        this.isSelected = true;
        this.cursorPosition = this.value.length;
        this.dropdownVisible = false;
        return true;
    }

    private updateColorFromHSL(): void {
        this.setValue(this.hslToHex(this.hue, this.saturation, this.lightness));
        this.propertyEditor.commitValueFromInput(this, this.value);
        this.levelDesigner.redraw();
    }

    public handleKeyEvent(event: KeyboardEvent): boolean {
        if (!this.isActive) return false;

        // Always prevent propagation for backspace and delete keys when input is active
        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.stopPropagation();
            event.preventDefault();
        }

        let handled = false;

        if (this.isSelected && event.key.length === 1) {
            // Replace entire selection with new character if it's a valid hex digit
            const hexRegex = /^[0-9A-Fa-f]$/;
            if (hexRegex.test(event.key)) {
                this.value = event.key;
                this.cursorPosition = 1;
                this.isSelected = false;
                handled = true;
            }
        } else {
            switch (event.key) {
                case 'ArrowLeft':
                    if (this.isSelected) {
                        this.isSelected = false;
                        this.cursorPosition = 0;
                    } else if (this.cursorPosition > 0) {
                        this.cursorPosition--;
                    }
                    handled = true;
                    break;
                case 'ArrowRight':
                    if (this.isSelected) {
                        this.isSelected = false;
                        this.cursorPosition = this.value.length;
                    } else if (this.cursorPosition < this.value.length) {
                        this.cursorPosition++;
                    }
                    handled = true;
                    break;
                case 'Backspace':
                    if (this.cursorPosition > 0) {
                        this.value = this.value.slice(0, this.cursorPosition - 1) + this.value.slice(this.cursorPosition);
                        this.cursorPosition--;
                    }
                    handled = true;
                    break;
                case 'Delete':
                    if (this.cursorPosition < this.value.length) {
                        this.value = this.value.slice(0, this.cursorPosition) + this.value.slice(this.cursorPosition + 1);
                    }
                    handled = true;
                    break;
                default:
                    // Only allow hex digits
                    const hexRegex = /^[0-9A-Fa-f]$/;
                    if (event.key.length === 1 && hexRegex.test(event.key) && this.value.length < 6) {
                        this.value = this.value.slice(0, this.cursorPosition) + event.key + this.value.slice(this.cursorPosition);
                        this.cursorPosition++;
                        handled = true;
                    }
            }
        }
        if(this.value instanceof MultipleValues) {
            this.displayValue = this.value.toString();
        }
        else{
            this.displayValue = this.value;
        }
        if (handled && this.value.length === 6) {
            this.parseColor(this.value);
            this.propertyEditor.commitValueFromInput(this, this.value);
            this.levelDesigner.redraw();
        }

        return handled;
    }

    public override setValue(value: string | MultipleValues): void {
        if(value instanceof MultipleValues) {
            this.displayValue = value.toString();
        }
        else {
            this.displayValue = value; 
            this.value = value.replace('#', '');
            this.parseColor(this.value);
        }
    }

    public focus(): void {
        this.isActive = true;
        this.isSelected = true;
        this.cursorPosition = this.value.length;
    }

    public blur(): void {
        this.isActive = false;
        this.isSelected = false;
        this.colorPickerVisible = false;
    }
} 
